import { getInsurerList } from '@/hooks/admin/useAdminInsurerList';
import { Insurer } from '@/types/admin-insurer';
import { useState } from 'react';

const EXACT_INSURER_FORMAT_REGEX = /^\d+-\d+$/;

interface InitialValues {
  insurerId?: number | null;
  insurerDisplay?: string;
  insurerTypeName?: string;
  insurerName?: string;
}

export const useInsurerFieldState = (siteType: string, initial?: InitialValues) => {
  const [insurerId, setInsurerId] = useState<number | null>(initial?.insurerId ?? null);
  const [insurerDisplay, setInsurerDisplayRaw] = useState(initial?.insurerDisplay ?? '');
  const [insurerTypeName, setInsurerTypeName] = useState(initial?.insurerTypeName ?? '');
  const [insurerName, setInsurerName] = useState(initial?.insurerName ?? '');
  const [insurerError, setInsurerError] = useState<string | null>(null);
  const [isInsurerModalOpen, setIsInsurerModalOpen] = useState(false);
  const [isOpeningModal, setIsOpeningModal] = useState(false);

  const setInsurerDisplay = (value: string) => {
    setInsurerDisplayRaw(value);
    setInsurerId(null);
    if (!value.trim()) {
      setInsurerTypeName('');
      setInsurerName('');
    }
  };

  const reset = (values: InitialValues) => {
    setInsurerId(values.insurerId ?? null);
    setInsurerDisplayRaw(values.insurerDisplay ?? '');
    setInsurerTypeName(values.insurerTypeName ?? '');
    setInsurerName(values.insurerName ?? '');
    setInsurerError(null);
  };

  const handleInsurerSelect = (insurer: Insurer) => {
    setInsurerId(insurer.id);
    setInsurerDisplayRaw(`${insurer.insurer_number}-${insurer.sub_number}`);
    setInsurerTypeName(insurer.insurer_type?.name ?? '');
    setInsurerName(insurer.insurer_name ?? '');
    setInsurerError(null);
    setIsInsurerModalOpen(false);
  };

  const handleOpenInsurerModal = async () => {
    const trimmed = insurerDisplay.trim();
    if (EXACT_INSURER_FORMAT_REGEX.test(trimmed)) {
      setIsOpeningModal(true);
      try {
        const res = await getInsurerList(siteType, {
          search_insurer_number: trimmed,
          per_page: '100',
        });
        const list = res.data.data;
        if (list.length === 1) {
          handleInsurerSelect(list[0]);
          return;
        }
      } catch {
        // fall through to open modal
      } finally {
        setIsOpeningModal(false);
      }
    }
    setIsInsurerModalOpen(true);
  };

  const resolveInsurer = (id: number, typeName: string, name: string) => {
    setInsurerId(id);
    setInsurerTypeName(typeName);
    setInsurerName(name);
  };

  return {
    insurerId,
    insurerDisplay,
    insurerTypeName,
    insurerName,
    insurerError,
    setInsurerError,
    isInsurerModalOpen,
    setIsInsurerModalOpen,
    isOpeningModal,
    setInsurerDisplay,
    handleInsurerSelect,
    handleOpenInsurerModal,
    resolveInsurer,
    resetInsurerField: reset,
  };
};
